/* Copyright (C) 2023 Advenio Tecnosys Pvt. Ltd.

*        All Rights Reserved

*

 * NOTICE: 

 * All information contained herein is, and remains

* the property of Advenio Tecnosys and its suppliers,

* if any.  The intellectual and technical concepts contained

* herein are proprietary to Advenio Tecnosys

* and its suppliers and may be covered by Indian, U.S. and Foreign Patents,

* patents in process, and are protected by trade secret or copyright law.

* Appropriation, redistribution, dissemination of this information or reproduction

* of this material is strictly forbidden unless prior written permission is obtained

* from Advenio Tecnosys Pvt. Ltd.

*

* Author: < Sanket, Mylavarapu Sarath Sai, Amal Sabu, Ujjwal Singh >

*/

import { client } from "actions";
import {
  lookupTable,
  statusesWeWontGenerateReportFor,
  statusesToGetStatusNamesFor,
} from "data/data";
import { baseURL } from "database/onlineDb/baseUrl";
import { systemicAssociations } from "data/data";

const DATA_URL_PREFIX = "data:";

// Wire-only payload sanitizer for PUT /patient and POST/PUT /patient-reg.
// The Redux store, Dexie rows and in-session UI keep the original values.
export const stripRetinalImageBase64FromWire = (patient) => {
  const details = patient?.retinalImagesData?.retinalImagesDetails;
  if (
    !details ||
    (details.imageLeftEyeData === undefined &&
      details.imageRightEyeData === undefined)
  ) {
    return patient;
  }
  return {
    ...patient,
    retinalImagesData: {
      ...patient.retinalImagesData,
      retinalImagesDetails: {
        ...details,
        imageLeftEyeData: undefined,
        imageRightEyeData: undefined,
      },
    },
  };
};

const stripDataUrlStringValues = (entry) => {
  if (!entry || typeof entry !== "object") {
    return entry;
  }
  let changed = false;
  const next = {};
  Object.keys(entry).forEach((key) => {
    const value = entry[key];
    if (typeof value === "string" && value.indexOf(DATA_URL_PREFIX) === 0) {
      changed = true;
      return;
    }
    next[key] = value;
  });
  return changed ? next : entry;
};

export const stripInlineBase64FromWire = (patient) => {
  if (!patient || typeof patient !== "object") {
    return patient;
  }
  const retinalStripped = stripRetinalImageBase64FromWire(patient);
  const clinicalConditionData = patient.clinicalConditionData;
  if (!clinicalConditionData || typeof clinicalConditionData !== "object") {
    return retinalStripped;
  }
  let clinicalChanged = false;
  const nextClinicalConditionData = {};
  Object.keys(clinicalConditionData).forEach((key) => {
    const group = clinicalConditionData[key];
    if (!Array.isArray(group)) {
      nextClinicalConditionData[key] = group;
      return;
    }
    let groupChanged = false;
    const nextGroup = group.map((entry) => {
      const strippedEntry = stripDataUrlStringValues(entry);
      if (strippedEntry !== entry) {
        groupChanged = true;
      }
      return strippedEntry;
    });
    if (groupChanged) {
      clinicalChanged = true;
      nextClinicalConditionData[key] = nextGroup;
    } else {
      nextClinicalConditionData[key] = group;
    }
  });
  if (!clinicalChanged) {
    return retinalStripped;
  }
  if (retinalStripped === patient) {
    return { ...patient, clinicalConditionData: nextClinicalConditionData };
  }
  return { ...retinalStripped, clinicalConditionData: nextClinicalConditionData };
};

export const getUserProfile = () => {
  return JSON.parse(sessionStorage.getItem("authentication"));
};

const moment = require("moment");
// To check if Object is empty
export const checkIfObjectIsEmpty = (obj) => {
  if (obj?.prototype !== {}.prototype)
    throw Error(`${JSON.stringify(obj)} is not an Object`);
  return Object.keys(obj).length === 0;
};

// Bulk-Upload.js
export const folderPathExtractor = (path) => {
  let separator;

  if (path.includes("/")) {
    separator = "/";
  } else if (path.includes("\\")) {
    separator = "\\";
  }
  const pathStringSplitWithoutExtensionSplitWithSeparator = path
    .split(".")?.[0]
    ?.split(separator);

  return pathStringSplitWithoutExtensionSplitWithSeparator
    ?.slice(0, pathStringSplitWithoutExtensionSplitWithSeparator.length - 1)
    .join(separator);
};

// View Images start author:Sai
// export const getThumbnailWorker = async (file) => {
//   try {
//     // Create a new canvas element
//     const canvas = new OffscreenCanvas(1000, 800);
//     // Create a new 2D context for the canvas
//     const ctx = canvas.getContext("2d");
//     // Try to create an image bitmap from the file
//     const buffer = await file.arrayBuffer();
//     const img = await createImageBitmap(new Blob([buffer]));
//     // Draw the original image to the canvas
//     ctx.drawImage(img, 0, 0);
//     // Create a new image bitmap from the canvas
//     const resized = await createImageBitmap(canvas, 0, 0, 50, 40);
//     ctx.drawImage(resized, 0, 0, 50, 40);

//     // Convert the canvas to a blob
//     const blob = await canvas.convertToBlob(canvas, "image/png");

//     // Get the data URL for the blob
//     const dataURL = await new Promise((resolve, reject) => {
//       const reader = new FileReader();
//       reader.onloadend = () => {
//         resolve(reader.result);
//       };
//       reader.onerror = reject;
//       reader.readAsDataURL(blob);
//     });
//     return dataURL;
//   } catch (error) {
//     console.log("thumbnail generation error", error);
//     return "";
//   }
// };

export const getThumbnail = async (file) => {
  try {
    const resizeImage = require("resize-image");
    return new Promise((resolve, reject) => {
      let img = new Image();
      img.onload = () =>
        resolve(resizeImage.resize(img, 50, 50, resizeImage.PNG));
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  } catch (error) {
    console.log("thumbnail generation error", error);
    return "";
  }
};

export const toDataURL = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return "";
    }
    const blob = await response.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error("toDataURL error", err);
    return "";
  }
};

// export const toDataURL = async (url) => {
//   try {
//     const response = await fetch(url);
//     const blob = await response.blob();

//     return await new Promise((resolve, reject) => {
//       const reader = new FileReader();
//       reader.onloadend = () => resolve(reader.result);
//       reader.onerror = reject;
//       reader.readAsDataURL(blob);
//     });
//   } catch (err) {
//     console.error("toDataURL error", err);
//   }
// };
export const getImageAspectRatio = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.src = url;
    img.onload = ({ target }) => {
      resolve(target.width / target.height);
    };
    img.onerror = () => {
      reject({ message: "An error occurred while loading the image." });
    };
  }).catch((err) => {
    console.error("getImageAspectRatio error", err);
    return 1;
  });


  export const getImageAspectRatioImage = async (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.src = url;
      img.onload = ({ target }) => {
        resolve([target.width, target.height]);
      };
      img.onerror = () => {
        reject({ message: "An error occurred while loading the image." });
      };
    }).catch((err) => {
      console.error("getImageAspectRatio error", err);
      return [1, 1];
    });

export function dataURLtoFile(dataurl, filename, options = {}, fileExtension) {
  const arr = dataurl.split(",");
  const bstr = atob(arr[1]);
  let n = bstr.length;
  let u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  options = fileExtension ? { ...options, type: fileExtension } : options;
  const file = new File([u8arr], filename, {
    ...options,
  });
  return file;
}

export const urlToFile = async (url, guid, options = {}) => {
  try {
    const fileExtension = url.split(".")?.[url.split(".").length - 1];
    options.type = `image/${fileExtension}`;
    const dataUrl = await toDataURL(url);
    const file = dataURLtoFile(dataUrl, guid, options);
    return file;
  } catch (error) {
    console.error(error);
  }
};

export const dataURLtoBlob = (dataurl) => {
  let arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  const blob = new Blob([u8arr], { type: "image/png" });
  const objectUrl = URL.createObjectURL(blob);
  return objectUrl;
};

export const imgUrlToThumbnailUrl = async (url) => {
  try {
    const dataUrl = await toDataURL(url);
    // console.log("Here is Base64 Url", dataUrl);
    const fileData = dataURLtoFile(dataUrl, "thumbnail");
    // console.log("Here is JavaScript File Object", fileData);
    const thumbnailUrl = await getThumbnail(fileData);
    return thumbnailUrl;
  } catch (err) {
    console.log("imgUrlToThumbnailUrl error", err);
  }
};

// export const urlToImageFileAndThumbnailWorker = async (url) => {
//   const dataStringUrl = await toDataURL(url);
//   const imageFile = dataURLtoFile(dataStringUrl, {}, ".png");
//   const thumbnailUrl = await getThumbnailWorker(imageFile);
//   return { dataStringUrl, thumbnailUrl };
// };
export const urlToImageFileAndThumbnail = async (url) => {
  const dataStringUrl = await toDataURL(url);
  const imageFile = dataURLtoFile(dataStringUrl, {}, ".png");
  const thumbnailUrl = await getThumbnail(imageFile);
  return { dataStringUrl, thumbnailUrl };
};

// View Images End

// export const checkReportLimit = (uploadedCount, presentStatus) => {
//  if (presentStatus === "Waiting" || presentStatus === "Server Unavailable") {
//   return null;
//  }
//  let data = null;
//  try {
//   data = JSON.parse(sessionStorage.getItem("addUserDetails")).license;
//  } catch (err) {
//   data = null;
//  }

//  //  if (new Date(data.end_date).getTime() < new Date().getTime()) {
//  //   return {
//  //    message:
//  //     "Your License have been expired. To renew license please contact Advenio Technosys.",
//  //    title: "License Expired!",
//  //    type: "licenseExpired",
//  //   };
//  //  }
//  if (!data.buffer_count || typeof data.buffer_count !== "number")
//   data.buffer_count = 0;
//  let reports_difference =
//   data.total_reports -
//   (data.reports_generated + data.buffer_count + uploadedCount);

//  if (reports_difference < 0) {
//   return {
//    message: "You Have Exhausted The Report Generation Limit",
//    title: "Report Limit Exceeded",
//    type: "reportLimitExceeded",
//   };
//  }
//  if (reports_difference <= 10) {
//   return {
//    message: `You have ${
//     reports_difference + 1
//    } Reports Remaining\n. Do You Wish to proceed`,
//    title: "Report Limit About To Exceed",
//    type: "reportLimitBelowTen",
//   };
//  }
//  return null;
// };

export const updateReportCount = (uploadedCount, presentStatus, cb) => {
  if (presentStatus === "Waiting" || presentStatus === "Server Unavailable") {
    cb(true);
    return;
  }
  //  commented code is about updating the report generation count which we won't be using anymore to stop user from opening the report.
  //  let data = null;
  //  try {
  //   data = JSON.parse(sessionStorage.getItem("addUserDetails"))?.license;
  //  } catch (err) {
  //   data = null;
  //  }
  //  if (!data) cb(false);
  //  if (!data.buffer_count || typeof data.buffer_count !== "number")
  //   data.buffer_count = 0;

  //  if (
  //   data.reports_generated + data.buffer_count + uploadedCount >
  //   data.total_reports
  //  ) {
  //   cb(false);
  //  }
  else {
    // client
    //  .put(
    //   `/license/${getUserDetailsFromSessionStorage()?.orgz_id}/report`,
    //   {
    //    new_reports: uploadedCount + data.buffer_count,
    //   },
    //   {
    //    headers: { authorization: getUserProfile().authToken },
    //   }
    //  )
    //  .then((res) => {
    // let new_data = JSON.parse(sessionStorage.getItem("addUserDetails"));
    // new_data.license.reports_generated += data.buffer_count + uploadedCount;
    // new_data.license.buffer_count = 0;
    // sessionStorage.setItem("addUserDetails", JSON.stringify(new_data));
    return cb(true);
    //  })
    //  .catch((err) => {
    //   let new_data = JSON.parse(sessionStorage.getItem("addUserDetails"));
    //   if (
    //    !new_data.license.buffer_count ||
    //    typeof new_data.license.buffer_count !== "number"
    //   )
    //    new_data.license.buffer_count = 0;
    //   new_data.license.buffer_count += uploadedCount;
    //   sessionStorage.setItem("addUserDetails", JSON.stringify(new_data));
    //   console.log("update report count error", err);
    //   return cb(true);
    //  });
  }
};

export const getDiseaseName = (priority, array) => {
  let value = "";
  for (let i = 0; i < array.length; i++) {
    if (array[i].priority === priority) {
      value = array[i].statusName;
    }
  }
  return value;
};

export const getStatusByAlgo = (algo, status) => {
  let value = [];
  for (let i = 0; i < status.length; i++) {
    if (status[i].Model === algo) {
      value.push(status[i].status);
    }
  }
  return value;
};

const getPriority = (label, array) => {
  let value = "";
  for (let i = 0; i < array.length; i++) {
    if (array[i].status === label) {
      value = array[i].priority;
    }
  }
  return value;
};

const getStatus = (priority, array) => {
  let value = "";
  for (let i = 0; i < array.length; i++) {
    if (array[i].priority === priority) {
      value = array[i].status;
    }
  }
  return value;
};

export const getOverAllReport = (leftArr, rightArr) => {
  const statusArrLeft = [];
  const statusArrRight = [];

  leftArr.forEach((img) => {
    statusArrLeft.push(getPriority(img.data.status, lookupTable));
  });

  rightArr.forEach((img) =>
    statusArrRight.push(getPriority(img.data.status, lookupTable))
  );

  const maxPriorityLeft = Math.max(...statusArrLeft);
  const maxPriorityRight = Math.max(...statusArrRight);

  // const leftEyeStatus = getStatus(maxPriorityLeft, lookupTable);
  // const rightEyeStatus = getStatus(maxPriorityRight, lookupTable);
  let tempArr = [maxPriorityLeft, maxPriorityRight];

  // let priorityArr = [];
  // tempArr.forEach((item) => {
  //   priorityArr.push(getPriority(item.overAllresult, lookupTable));
  // });
  let maxPriority = Math.max(...tempArr);
  return getStatus(maxPriority, lookupTable);
};

export const getFinalStatus = (status, array = lookupTable, algorithms) => {
  if (typeof algorithms === "string") {
    algorithms = [algorithms];
  } else if (!Array.isArray(algorithms)) {
    algorithms = ["Normal-Dr-NonDr-Pipeline-2"];
  } else if (algorithms.length === 0) {
    algorithms = ["Normal-Dr-NonDr-Pipeline-2"];
  }
  let tempArr = [];
  let statusByAlgo = [];
  algorithms.map((item) => {
    statusByAlgo.push(...getStatusByAlgo(item, status));
  });
  statusByAlgo.map((item) => {
    tempArr.push(getPriority(item, array));
  });
  let maxPriority = Math.max(...tempArr);
  const diseaseName = getDiseaseName(maxPriority, array);
  return diseaseName;
};

//  getFinalStatusWithModifiedDrStatuses is a variation of the above getFinalStatus function. It should be used carefully. Many places in the code still rely on the NPDR/D2 or PDR/D1 statuses. So it is advised that this function should be used only in the places where we need to show the "Diabetic Retinopathy" in the UI in cases of NPDR and PDR, i.e. only in UI related code.
export const getFinalStatusWithModifiedDrStatuses = (
  statusFromRow,
  lookupTable,
  algos
) => {
  const DiabeticRetinopathy = "Diabetic Retinopathy";
  const ProliferativeDiabeticRetinopathy = "Proliferative Diabetic Retinopathy";
  const NonProliferativeDiabeticRetinopathy =
    "Non-Proliferative Diabetic Retinopathy";
  const status = getFinalStatus(statusFromRow, lookupTable, algos);
  if (
    status.includes(ProliferativeDiabeticRetinopathy) ||
    status.includes(NonProliferativeDiabeticRetinopathy)
  ) {
    return "Referrable. " + DiabeticRetinopathy;
  } else return status;
};

export const getStatusNameForConsolidatedReport = (input) => {
  if (statusesToGetStatusNamesFor.indexOf(input) > -1)
    return lookupTable.filter(
      (lookupTableItem) => lookupTableItem.status === input
    )?.[0]?.statusName;
};
// Capitalises the statuses after we modify to give dr_consent in view images/view patients
export function truncateAndCapitalizeOrGetStatusNameForStringStatus(input) {
  if (statusesToGetStatusNamesFor.indexOf(input) > -1)
    return lookupTable.filter(
      (lookupTableItem) => lookupTableItem.status === input
    )?.[0]?.statusName;

  if (input.length > 35) {
    const inputToSub = input.substr(0, 32) + "...";
    const words = inputToSub.split(" ");
    const CapitalizedWords = [];
    words.forEach((element) => {
      CapitalizedWords.push(
        element[0] + element.slice(1, element.length).toLowerCase()
      );
    });
    return CapitalizedWords.join(" ");
  } else {
    const words = input.split(" ");
    const CapitalizedWords = [];
    words.forEach((element) => {
      CapitalizedWords.push(
        element[0] + element.slice(1, element.length).toLowerCase()
      );
    });
    return CapitalizedWords.join(" ");
  }
}

export const diseaseListFromSessionStorage = () =>
  sessionStorage.getItem("disease-list")
    ? JSON.parse(sessionStorage.getItem("disease-list"))
    : [];

export const clinicalFeaturesListFromSessionStorage = () =>
  sessionStorage.getItem("clinical-features-list")
    ? JSON.parse(sessionStorage.getItem("clinical-features-list"))
    : [];

export const secondaryFindingsListFromSessionStorage = () =>
  sessionStorage.getItem("secondary-findings-list")
    ? JSON.parse(sessionStorage.getItem("secondary-findings-list"))
    : [];

export const diagnosticTestsFromSessionStorage = () =>
  sessionStorage.getItem("diagnostic-tests-list")
    ? JSON.parse(sessionStorage.getItem("diagnostic-tests-list"))
    : [];

export const recommendationsListFromSessionStorage = () =>
  sessionStorage.getItem("recommendations-list")
    ? JSON.parse(sessionStorage.getItem("recommendations-list"))
    : [];

export const differentialDiagnosisListFromSessionStorage = () =>
  sessionStorage.getItem("differential-diagnosis-list")
    ? JSON.parse(sessionStorage.getItem("differential-diagnosis-list"))
    : [];

export const getAuthenticationDetailsFromSessionStorage = () => {
  const authenticationInfoFromSessionStorage =
    sessionStorage.getItem("authentication");

  return authenticationInfoFromSessionStorage !== undefined &&
    authenticationInfoFromSessionStorage !== "undefined" &&
    authenticationInfoFromSessionStorage !== null &&
    authenticationInfoFromSessionStorage !== "null" &&
    authenticationInfoFromSessionStorage !== ""
    ? JSON.parse(sessionStorage.getItem("authentication"))
    : "";
};

export const getAuthTokenFromSessionStorage = () => {
  const authDetails = getAuthenticationDetailsFromSessionStorage();
  return authDetails?.authToken || "";
};

// export const getFirstNameOfCurrentUserFromSessionStorage = () =>
//   getUserDetailsFromSessionStorage()?.fname || "";

export const removeItemsFromSessionStorageToLogout = () => {
  sessionStorage.getItem("authentication") &&
    sessionStorage.removeItem("authentication");

  sessionStorage.getItem("view-patient-bu-page-num") &&
    sessionStorage.removeItem("view-patient-bu-page-num");
  sessionStorage.getItem("view-patient-page-num") &&
    sessionStorage.removeItem("view-patient-page-num");
  sessionStorage.getItem("symptomsList") &&
    sessionStorage.removeItem("symptomsList");
  // To remove the processed Images count from sessionStorage upon Logout, author :Sai
  sessionStorage.getItem("processCount") &&
    sessionStorage.removeItem("processCount");
  sessionStorage.getItem("disease-list") &&
    sessionStorage.removeItem("disease-list");
};

// export const getEmailIdOfTheCurrentUser = () =>
//   getUserDetailsFromSessionStorage()?.email || "";

export const systemicAssociationsFetcher = async (
  leftEyeDocDis,
  leftEyeAiStatus,
  rightDocDis,
  rightEyeAiStatus
) => {
  try {
    const leftEyeDisease = leftEyeDocDis || leftEyeAiStatus;
    const rightEyeDisease = rightDocDis || rightEyeAiStatus;

    const localSysAssocsList = [];
    let localLeftEyeSysAsscocsPresent = false;
    let localRightEyeSysAsscocsPresent = false;
    if (systemicAssociations[leftEyeDisease]) {
      localLeftEyeSysAsscocsPresent = true;
      localSysAssocsList.push(systemicAssociations[leftEyeDisease]);
    }
    if (systemicAssociations[rightEyeDisease]) {
      localRightEyeSysAsscocsPresent = true;
      localSysAssocsList.push(systemicAssociations[rightEyeDisease]);
    }
    if (localLeftEyeSysAsscocsPresent && localRightEyeSysAsscocsPresent) {
      return Array.from(new Set(localSysAssocsList)).join(", ");
    }
    const diseaseListWithoutDuplicates = Array.from(
      new Set([
        !localLeftEyeSysAsscocsPresent ? leftEyeDisease : null,
        !localRightEyeSysAsscocsPresent ? rightEyeDisease : null,
      ])
    ).filter(
      (dis) => dis && statusesWeWontGenerateReportFor.indexOf(dis) === -1
    );
    const sysAssocsList = await client.get(`${baseURL}/sys-assoc-list`, {
      headers: {
        Authorization: getAuthTokenFromSessionStorage(),
        diseases: diseaseListWithoutDuplicates,
      },
    });
    const backendSysAssocsList = sysAssocsList?.data?.sysAssocsList;
    if (localSysAssocsList.length) {
      const finalSysAssocsList = Array.from(
        new Set([...localSysAssocsList, backendSysAssocsList])
      );
      return finalSysAssocsList.join(", ");
    }
    return backendSysAssocsList;
  } catch (err) {
    console.log("sys assoc list get error", err);
    return "";
  }
};

export const prescriptionListFetcher = async (id) => {
  try {
    const prescriptionList = await client.get(
      `${baseURL}/prescription-list/${id}`,
      {
        headers: {
          Authorization: getAuthTokenFromSessionStorage(),
        },
      }
    );

    if (prescriptionList.data.success) {
      return prescriptionList.data.prescriptionList;
    } else {
      return [];
    }
  } catch (err) {
    return [];
  }
};

export const getAddUserSettingsFromSessionStorage = (item) => {
  const addUserSettingsFromSessionStorage =
    sessionStorage.getItem("addUserSettings");
  let parsedAddUserSettings = null;
  if (addUserSettingsFromSessionStorage) {
    parsedAddUserSettings = JSON.parse(addUserSettingsFromSessionStorage);
  }
  if (!parsedAddUserSettings) {
    return "";
  }
  if (item && parsedAddUserSettings.hasOwnProperty(item)) {
    return parsedAddUserSettings[item];
  }
  return parsedAddUserSettings;
};

export const getDiagnosticTests = async (
  mrnSpecificDrAnnotationDetails,
  statuscode
) => {
  const { drTests } = mrnSpecificDrAnnotationDetails;
  const testOptionsDefault = {};
  try {
    const diagnosticTests = diagnosticTestsFromSessionStorage();
    if (drTests) {
      const diagnosticTestsFromSession = Object.values(diagnosticTests).reduce(
        (acc, cv) => [...acc, ...cv],
        []
      );

      const drTestsIds = drTests.split(",").map((id) => parseInt(id));
      const filteredTests = diagnosticTestsFromSession.filter((test) =>
        drTestsIds.includes(test.id)
      );

      filteredTests.forEach((test) => {
        if (!testOptionsDefault.hasOwnProperty(test?.test_sub_category)) {
          testOptionsDefault[test?.test_sub_category] = [];
        }
        testOptionsDefault[test?.test_sub_category].push(test.test_name);
      });

      return testOptionsDefault;
    } else if (!drTests) {
      if (statuscode !== "NRML") {
        const getDefaultStatusAccordingToStatus = await client.get(
          `${baseURL}/diagnostic-test`,
          {
            headers: {
              Authorization: getAuthTokenFromSessionStorage(),
              statuscode: statuscode,
            },
          }
        );
        const defaultTests = getDefaultStatusAccordingToStatus.data;
        defaultTests.forEach((test) => {
          if (!testOptionsDefault.hasOwnProperty(test?.test_sub_category)) {
            testOptionsDefault[test?.test_sub_category] = [];
          }
          testOptionsDefault[test?.test_sub_category].push(test.test_name);
        });

        return testOptionsDefault;
      }
    }
  } catch (err) {
    console.log(err);
  }
};

export const getDrRecomendations = (mrnSpecificDrAnnotationDetails) => {
  let { recommendation } = mrnSpecificDrAnnotationDetails;

  if (Array.isArray(recommendation)) {
    recommendation = recommendation.join(",");
  }
  const recommendationsFromSession = recommendationsListFromSessionStorage();
  if (recommendation) {
    const drRecommendationsIds = recommendation
      .split(",")
      .map((id) => parseInt(id));
    const filteredRecommendations = recommendationsFromSession.filter((test) =>
      drRecommendationsIds.includes(test.id)
    );
    return filteredRecommendations;
  } else {
    return [];
  }
};

const getShortNameFromStatus = (label, array) => {
  let value = "";
  for (let i = 0; i < array.length; i++) {
    if (value !== "") break;
    if (array[i].status === label) {
      value = array[i].shortName;
    }
  }
  return value;
};

export const getContent = (status, lookupTable, contentList) => {
  // console.log("getContent params", status, contentList);
  // Disabled the statuses related to these in the lookup table at the request of Dr.Mausumi
  // if (status === "NRDR") {
  //   return contentList.filter(
  //     (reportContentItem) => reportContentItem.acronym === "D2"
  //   );
  // } else if (status === "RDR") {
  //   return contentList.filter(
  //     (reportContentItem) => reportContentItem.acronym === "D1"
  //   );
  // } else {
  return contentList.filter(
    (reportContentItem) => reportContentItem.name === status
  );
  // }
};

// export function generateCurrentDate() {
//   return moment(new Date()).format("YYYY-MM-DD HH:mm:ss");
// }
